/* ═══════════════════════════════════════════════════════════════════════════
   Time
   A time in the past cannot be booked. The rules live here and not in the
   component, so they can be tested without a browser.

   NOTICE is how much time there must be left before an appointment starts.
   Zero means it only has to lie ahead. Each business sets its own once the
   backend can answer for it — we are not inventing one here.
   ═══════════════════════════════════════════════════════════════════════ */

export const NOTICE_MIN = 0;

export function iso(d) {
  return (
    d.getFullYear() +
    '-' + String(d.getMonth() + 1).padStart(2, '0') +
    '-' + String(d.getDate()).padStart(2, '0')
  );
}

export function fromISO(s) {
  const p = String(s).split('-').map(Number);
  return new Date(p[0], p[1] - 1, p[2], 12);
}

/** "09:30" → 570 */
export function toMinutes(t) {
  const m = String(t || '').match(/^(\d{1,2})[:.](\d{2})/);
  return m ? +m[1] * 60 + +m[2] : null;
}

/** 570 → "09:30" */
export function fromMinutes(m) {
  return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
}

export function isToday(dateISO, now = new Date()) {
  return dateISO === iso(now);
}

/** Is this date behind us? Yesterday is, whatever o'clock it says. */
export function isPastDay(dateISO, now = new Date()) {
  const d = String(dateISO || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  return d < iso(now);
}

/**
 * Has this time already gone by?
 *
 * Two questions in one, and for a long time this answered only the second:
 *
 *     if (!isToday(dateISO, now)) return false;
 *
 * — so a time on a date that had already been and gone was never past. Every
 * guard in the app reads this function, which meant every one of them was
 * open on a past DATE while being shut on a past HOUR: `confirmBooking` let
 * a reservation be written into last Tuesday, `slots()` marked nothing as
 * gone, `fetchSittings` offered a restaurant's evening a week after it had
 * been served, and the desk's day view drew every gap on a past day as free
 * to press. Nothing was wrong with any of those screens. They were asking
 * the right question and being told the wrong answer.
 *
 * A date before today is past at every hour of it. On today, it is the clock
 * that decides — plus `notice`, which is how much of a head start a business
 * wants and is its own setting, not ours.
 */
export function isPast(dateISO, time, now = new Date(), notice = NOTICE_MIN) {
  if (isPastDay(dateISO, now)) return true;
  if (!isToday(dateISO, now)) return false;
  const m = toMinutes(time);
  /* A date with no time on it: the day itself decides, and today has not
     gone by. */
  if (m == null) return false;
  return m <= now.getHours() * 60 + now.getMinutes() + notice;
}

/* ── Opening hours ────────────────────────────────────────────────────────
   Every business in the catalogue publishes its hours, in Norwegian, as a
   line of text: "Man–fre 09:00–18:00, lør 10:00–16:00". Until now nothing
   read it. `slots()` ran 08:30–17:00 for all thirty-four of them, which means
   a restaurant open ten to eight offered a table at half past eight in the
   morning and none at seven in the evening, and a salon open until nine
   showed its last time at five.

   So the line is parsed. It is a display string rather than structured data,
   which is a fact about the catalogue and not a reason to ignore it — when
   Chamcore answers with real opening hours this parser is what turns them
   into a day, and a business whose line cannot be read falls back to the old
   constant rather than losing its calendar.

   The days, in the order Norwegian writes them, with both spellings that
   appear in the wild. */
const DAYS = ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'];
const DAY_ALIAS = { lor: 'lør', son: 'søn', sun: 'søn', tors: 'tor' };

/* Round the clock. A damage-restoration firm writes "Døgnvakt, alle dager"
   and means it — there is no range to parse and the answer is every hour of
   every day, which is the opposite of what a failed parse would have said. */
const ALL_HOURS = { open: 0, close: 24 * 60 };
const ALWAYS = /(døgnvakt|døgnåpent|24\/7|hele døgnet|alltid åpent)/;

const dayIndex = word => {
  const w = String(word || '').toLowerCase().replace(/\./g, '').slice(0, 4);
  const key = DAY_ALIAS[w] || DAY_ALIAS[w.slice(0, 3)] || w.slice(0, 3);
  return DAYS.indexOf(key);
};

/**
 * "Man–fre 09:00–18:00, lør 10:00–16:00" → seven entries, Monday first, each
 * `{open, close}` in minutes or null for a day the business is shut.
 * Unparseable input gives null, and the caller falls back.
 */
export function openingHours(line) {
  const text = String(line || '').toLowerCase();
  if (!text.trim()) return null;
  if (ALWAYS.test(text)) return DAYS.map(() => ({ ...ALL_HOURS }));

  const week = [null, null, null, null, null, null, null];
  let found = false;

  /* Each clause is "<day or day range> <from>–<to>". The dash may be an
     en dash, an em dash or a hyphen, and the separator between clauses is a
     comma or a semicolon. */
  for (const clause of text.split(/[,;]/)) {
    const m = clause.trim().match(
      /^([a-zæøå.]+)\s*(?:[-–—]\s*([a-zæøå.]+))?\s+(\d{1,2}[:.]\d{2})\s*[-–—]\s*(\d{1,2}[:.]\d{2})/
    );
    if (!m) continue;
    const from = dayIndex(m[1]);
    if (from < 0) continue;
    const to = m[2] ? dayIndex(m[2]) : from;
    const open = toMinutes(m[3]);
    const close = toMinutes(m[4]);
    if (open == null || close == null || close <= open) continue;

    /* A range may wrap the week — "fre–man" is Friday through Monday. */
    const end = to < 0 ? from : to;
    let i = from;
    for (;;) {
      week[i] = { open, close };
      found = true;
      if (i === end) break;
      i = (i + 1) % 7;
    }
  }
  return found ? week : null;
}

/** Monday-first weekday index for a date, so it lines up with `openingHours`. */
export function weekdayOf(dateISO) {
  const d = fromISO(dateISO);
  return (d.getDay() + 6) % 7;
}

/** The hours for one date, or null when the business is shut that day. */
export function hoursOn(dateISO, line) {
  const week = openingHours(line);
  if (!week) return null;
  return week[weekdayOf(dateISO)];
}

/** The fallback, for a business whose hours cannot be read. */
export const DEFAULT_HOURS = { open: 8 * 60 + 30, close: 17 * 60 };

/**
 * The available slots for one day.
 * The business's own opening hours, in quarters. Taken periods are
 * subtracted. Slots in the past are not removed — they are marked, because an
 * empty list explains nothing.
 *
 * `hours` is the business's `opening_hours` line. Left out, the old constant
 * applies, so every existing caller keeps working while it is threaded
 * through.
 *
 * A day the business is shut returns an empty list. That is a real answer and
 * the booking screen says so, rather than offering times nobody will be there
 * for.
 */
export function slots({ dateISO, duration = 30, taken = [], now = new Date(), hours }) {
  /* Three answers, and the middle one matters most:
       no `hours` at all      → the old constant, so every existing caller works
       a line we cannot read  → the old constant, NOT a closed business
       a line we can read     → the day it names, which may be closed
     The first version of this closed any business whose line did not parse,
     which shut Oslo Skadestopp — "Døgnvakt, alle dager", a 24-hour emergency
     service — completely. A line we cannot read means we do not know, and
     "we do not know" must never render as "they are shut". */
  const week = hours === undefined ? null : openingHours(hours);
  const today = hours === undefined || !week ? DEFAULT_HOURS : week[weekdayOf(dateISO)];
  if (!today) return [];
  const { open, close } = today;
  const out = [];
  for (let t = open; t + duration <= close; t += 15) {
    const s = t, e = t + duration;
    if (taken.some(([a, b]) => s < b && e > a)) continue;
    const at = fromMinutes(s);
    out.push({
      start: at,
      end: fromMinutes(e),
      past: isPast(dateISO, at, now),
    });
  }
  return out;
}

/**
 * The same salon must give the same day every time. Nothing that gets shown
 * is allowed to be random. Replaced by real calendar data from the backend.
 */
export function takenDemo(key) {
  let h = 0;
  for (let i = 0; i < String(key).length; i++)
    h = (h * 31 + String(key).charCodeAt(i)) >>> 0;
  const f = (h % 3) * 15;
  return [
    [570 + f, 615 + f],
    [720, 765],
    [885 + (h % 2) * 15, 930 + (h % 2) * 15],
  ];
}

/** The days you can pick: today and forward. */
export function days(count = 21, now = new Date()) {
  const out = [];
  const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let i = 0; i < count; i++) {
    const d = new Date(d0);
    d.setDate(d0.getDate() + i);
    out.push({ iso: iso(d), date: d, today: i === 0 });
  }
  return out;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

export function dateWords(dateISO) {
  const d = fromISO(dateISO);
  const s = WEEKDAYS[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()];
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function shortDate(dateISO) {
  const d = fromISO(dateISO);
  return d.getDate() + ' ' + MONTHS[d.getMonth()].slice(0, 3);
}
